'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { freebiesService } from '@/lib/freebies';
import type { Freebie } from '@/types/freebie';

export default function FreebiesContent() {
  const [freebies, setFreebies] = useState<Freebie[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await freebiesService.getActive();
        setFreebies(data);
      } catch (error) {
        console.error('Error loading freebies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const hasFreebies = useMemo(() => freebies.length > 0, [freebies]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10 md:py-14">
      <section className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-primary">
          <Icon name="SparklesIcon" size={14} />
          Resource Library
        </span>
        <h1 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
          Freebies Built for Real Growth
        </h1>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground">
          Explore premium playbooks, templates, and growth resources. Unlock by entering your email.
        </p>
      </section>

      {isLoading ? (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div key={item} className="h-72 rounded-2xl bg-muted animate-pulse" />
          ))}
        </section>
      ) : !hasFreebies ? (
        <section className="max-w-2xl mx-auto rounded-2xl border border-border bg-card p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground">No freebies available yet</h2>
          <p className="mt-2 text-muted-foreground">Please check back soon.</p>
        </section>
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {freebies.map((freebie) => (
            <Link
              key={freebie.id}
              href={`/freebies/${freebie.slug}`}
              className="group rounded-2xl border border-border bg-card overflow-hidden shadow-neutral transition-all duration-300 hover:-translate-y-1 hover:shadow-neutral-lg"
            >
              <div className="h-44 overflow-hidden">
                <AppImage
                  src={freebie.imageUrl}
                  alt={freebie.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <h2 className="font-heading text-xl font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                  {freebie.name}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{freebie.description}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-primary text-sm font-semibold">
                  View Freebie
                  <Icon name="ArrowRightIcon" size={16} />
                </div>
              </div>
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}
